/**
 * calc/holdings.ts — positions, P&L, and allocation aggregation.
 *
 * Trade-replay into current holdings (average-cost bookkeeping), net
 * contributions, market-value/P&L enrichment, and the sector/country allocation
 * + scenario-diff helpers. Cash arithmetic is delegated to the cashFlow kernel
 * so it stays consistent with the equity engine.
 */
import type { Trade, PriceStore } from './types';
import { fxRateFor, resolveTradeCurrency } from './fx';
import { type FxContext, cashFlowBase, cashDelta } from './cashFlow';
import { latestUsableEntry } from './priceBook';
import { amountRowSign, isAmountRow } from './tradeTypes';

/**
 * Positions at or below this share count are treated as closed. Fractional
 * buys/sells leave float residue (0.1+0.1+0.1−0.3 ≈ 4.4e-17), so exact
 * `<= 0` checks would keep phantom positions alive. Matches the epsilon
 * csvImport's detectOpeningHoldings already uses.
 */
export const SHARE_EPSILON = 1e-9;

export interface Holding {
  ticker: string;
  name: string;
  sector: string;
  country: string;
  /** Native (listing) currency. Optional so pre-FX callers and fixtures stay valid. */
  currency?: string;
  shares: number;
  totalCost: number;
  avgCost: number;
}

export interface HoldingWithPnL extends Holding {
  price: number | null;
  /** The date of `price` — the series' last entry — so a screen can say how
   *  old it is (WORK.md item 43: a stale price is shown and marked, never
   *  replaced). Null exactly when `price` is. */
  priceDate: string | null;
  marketValue: number | null;
  pnl: number | null;
  pnlPct: number | null;
  weight: number | null;
  /** True when the holding's currency has no rate to the base: it is left out
   *  of the totals (marketValue, pnl and weight null), never valued at 1. */
  fxMissing?: boolean;
}

export interface ComputedHoldings {
  holdings: Holding[];
  cash: number;
}

export function computeHoldings(
  trades: Trade[],
  startingCash: number,
  asOfDate: string | Date | null = null,
  fx?: FxContext,
): ComputedHoldings {
  const holdings: Record<string, Holding> = {};
  let cash = startingCash;
  const cutoff = asOfDate ? new Date(asOfDate).getTime() : Infinity;

  // Stable chronological sort: ISO date strings compare lexicographically and
  // Array.sort is stable, so same-date rows keep their entered order (a BUY before
  // a same-day SELL stays first). Money-in-first replayCompare is deliberately NOT
  // used here — like the tax lot replay it would turn a same-date BUY→SELL into an
  // oversell (see replayOrder.ts / ADR-0022). SPLITs alone are hoisted to the
  // front of their date: a split takes effect at the ex-date open, so same-date
  // trades execute at post-split prices — and replayCompare orders SPLIT first
  // too, so this keeps the holdings table and the value curve engine agreeing
  // on share counts. (A SPLIT moves no cash, so hoisting it cannot oversell.)
  const sorted = [...trades].sort((a, b) =>
    a.date.localeCompare(b.date) ||
    (a.type === 'SPLIT' ? 0 : 1) - (b.type === 'SPLIT' ? 0 : 1));

  for (const t of sorted) {
    if (new Date(t.date).getTime() > cutoff) break;
    // An amount row (DEPOSIT, WITHDRAWAL, FEE, INTEREST) moves cash only: a
    // FEE attributed to a ticker never opens, closes or re-costs that holding.
    if (isAmountRow(t.type)) {
      cash += cashDelta(t, fx);
      continue;
    }
    const ticker = t.ticker;
    // SPLIT: multiply held shares by the ratio (stored in `shares`) from the
    // ex-date forward; TOTAL cost basis and cash are unchanged (so per-share avg
    // cost divides by the ratio). Ignore a split with no live position so it
    // can't create a phantom 0-share holding.
    if (t.type === 'SPLIT') {
      const hs = holdings[ticker];
      if (hs && hs.shares > SHARE_EPSILON) hs.shares *= t.shares;
      continue;
    }
    const ccy = resolveTradeCurrency(t);
    if (!holdings[ticker]) {
      holdings[ticker] = {
        ticker,
        name: t.name || ticker,
        sector: t.sector || '',
        country: t.country || '',
        currency: ccy,
        shares: 0,
        totalCost: 0,
        avgCost: 0,
      };
    }
    // Convert this trade into base at the TRADE date. fx absent ⇒ rate 1.
    const rate = fxRateFor(fx, ccy, t.date);
    const grossValue = t.shares * t.price * rate;
    cash += cashDelta(t, fx);
    if (t.type === 'BUY') {
      // A buy's commission is part of its cost, as the tax replay and HMRC
      // (TCGA 1992 s38(1)) count it; leaving it out overstated every
      // unrealised P&L by the buys' fees (audit MONEY-11).
      holdings[ticker].totalCost += grossValue + (t.commission ?? 0) * rate;
      holdings[ticker].shares += t.shares;
    } else if (t.type === 'SELL') {
      // Proportional cost-basis reduction (DIVIDEND leaves the position).
      const h = holdings[ticker];
      const avg = h.shares > SHARE_EPSILON ? h.totalCost / h.shares : 0;
      h.totalCost -= avg * t.shares;
      h.shares -= t.shares;
    }
    // Drop non-positive positions whatever row produced them: a SELL-to-zero,
    // a fractional sell leaving float residue (epsilon), or a DIVIDEND paying
    // after the position was closed (pay-date lags ex-date) — the cash landed
    // above; a {shares: 0} row must not reach the holdings tables.
    if (holdings[ticker].shares <= SHARE_EPSILON) {
      delete holdings[ticker];
    }
  }

  for (const h of Object.values(holdings)) {
    h.avgCost = h.shares > SHARE_EPSILON ? h.totalCost / h.shares : 0;
  }

  return { holdings: Object.values(holdings), cash };
}

/** The opening cash a portfolio is valued from when none is recorded anywhere. */
export const DEFAULT_STARTING_CASH = 100_000;

/** The settings shape the opening-cash rule reads: the app's Settings and the edge's SettingsLike both fit. */
export interface StartingCashSettings {
  startingCash?: number | null;
  portfolios?: ReadonlyArray<{ id?: string; startingCash?: number | null } | null | undefined> | null;
}

/**
 * A portfolio's opening cash: its own `startingCash`, then the global legacy
 * field, then DEFAULT_STARTING_CASH — the precedence currency already uses.
 *
 * THE one spelling, for the app and the edge's shared view alike. Until
 * 2026-10-10 the app spelt it fourteen times two ways: Analytics and
 * Performance Summary read the fund's own value while PublicView, Projections,
 * Scenario, Tax and Monte Carlo read the GLOBAL one, so the L&G pension (its
 * own startingCash 0, the global 100,000) showed £100k of cash it never had —
 * a public total of £185,635 against £85,635, and a projection that left
 * 53.9% of the book out as "cash" (data audit DATA-2). The shared view
 * defaulted to 0 where the app defaulted to 100,000.
 */
export function resolveStartingCash(settings: StartingCashSettings | null | undefined, portfolioId?: string | null): number {
  // A stored blob is untrusted shape: the edge reads it raw, so a non-array is no portfolios.
  const list = Array.isArray(settings?.portfolios) ? settings.portfolios : [];
  const p = list.find((x) => x != null && x.id === portfolioId);
  const own = p?.startingCash;
  if (typeof own === 'number' && Number.isFinite(own)) return own;
  const global = settings?.startingCash;
  if (typeof global === 'number' && Number.isFinite(global)) return global;
  return DEFAULT_STARTING_CASH;
}

/**
 * Every code the trade log ever held: each ticker a BUY or SELL row names.
 *
 * `computeHoldings(...).holdings` answers what is held NOW, which is the
 * wrong population for anything that values the book on PAST dates — a code
 * sold out last year still had a value on every date it was held. The data
 * audit of 2026-10-10 (DATA-1) found the pension's main holding of 2019-2022,
 * sold out since, with no dated price in the app's book: its value read ~£0
 * for three years and the time-weighted index ended at -2.2e150. Cash
 * pseudo-tickers (`$…`) are never codes.
 */
export function everHeldTickers(trades: Iterable<Trade>): Set<string> {
  const out = new Set<string>();
  for (const t of trades) {
    if ((t.type === 'BUY' || t.type === 'SELL') && t.ticker && !t.ticker.startsWith('$')) {
      out.add(t.ticker);
    }
  }
  return out;
}

/**
 * Net contributions: opening cash + Σ deposits − Σ withdrawals, each converted
 * to base at its flow date. The capital actually put in, used as the Total
 * Return baseline so deposits/withdrawals don't read as gains/losses. `asOfDate`
 * bounds it to flows on/before that date, mirroring computeHoldings.
 */
export function netContributions(
  trades: Trade[],
  startingCash: number,
  asOfDate: string | Date | null = null,
  fx?: FxContext,
): number {
  const cutoff = asOfDate ? new Date(asOfDate).getTime() : Infinity;
  let net = startingCash;
  for (const t of trades) {
    if (new Date(t.date).getTime() > cutoff) continue;
    if (t.type === 'DEPOSIT') net += cashFlowBase(t, fx);
    else if (t.type === 'WITHDRAWAL') net -= cashFlowBase(t, fx);
  }
  return net;
}

/**
 * The fees the account paid, the interest it received and the tax withheld from
 * its income, each net of its refunds and converted to base at its own date,
 * over (from, asOf] — `from` null is the whole log.
 * Neither is a contribution: both are inside the return (net-of-fee, GIPS
 * 2020), so Performance Summary states the fees beside the return they were
 * taken from, and the gross-of-fee return adds them back.
 */
export function feesAndInterest(
  trades: Trade[],
  asOfDate: string | Date | null = null,
  fx?: FxContext,
  fromDate: string | null = null,
): { fees: number; interest: number; taxes: number } {
  const cutoff = asOfDate ? new Date(asOfDate).getTime() : Infinity;
  let fees = 0;
  let interest = 0;
  let taxes = 0;
  for (const t of trades) {
    if (new Date(t.date).getTime() > cutoff) continue;
    if (fromDate && t.date <= fromDate) continue;
    // A cost is counted net of its refunds (amountRowSign is −1 for the cost,
    // +1 for the refund), so a rebated fee is the fee the account bore.
    if (t.type === 'FEE') fees -= amountRowSign(t) * cashFlowBase(t, fx);
    else if (t.type === 'TAX') taxes -= amountRowSign(t) * cashFlowBase(t, fx);
    else if (t.type === 'INTEREST') interest += cashFlowBase(t, fx);
  }
  return { fees, interest, taxes };
}

/**
 * Given holdings and a priceStore, attach current price + market value + P&L.
 * Returns a copy. The "current" price is the series' latest finite, positive close. The displayed
 * `price` stays native; marketValue/pnl/weight convert to base. fx absent ⇒
 * identical to the pre-FX path.
 */
export function holdingsPnL(holdings: Holding[], prices: PriceStore, fx?: FxContext): {
  rows: HoldingWithPnL[];
  totalMarketValue: number;
} {
  const enriched: HoldingWithPnL[] = holdings.map((h) => {
    const last = latestUsableEntry(prices[h.ticker]);
    const price: number | null = last ? last.price : null;
    const priceDate: string | null = last ? last.date : null;
    const rate = fxRateFor(fx, resolveTradeCurrency(h), priceDate);
    // fxRateFor answers 0 only for a currency with no rate to the base.
    const fxMissing = rate === 0;
    const marketValue = price != null && !fxMissing ? h.shares * price * rate : null;
    const cost = h.shares * h.avgCost;
    const pnl = marketValue != null ? marketValue - cost : null;
    const pnlPct = pnl != null && cost > 0 ? pnl / cost : null;
    const row: HoldingWithPnL = { ...h, price, priceDate, marketValue, pnl, pnlPct, weight: null };
    if (fxMissing) row.fxMissing = true;
    return row;
  });

  const totalMarketValue = enriched.reduce(
    (sum, r) => sum + (r.marketValue ?? 0),
    0,
  );

  for (const r of enriched) {
    if (r.marketValue != null && totalMarketValue > 0) {
      r.weight = r.marketValue / totalMarketValue;
    }
  }

  return { rows: enriched, totalMarketValue };
}

/**
 * The held codes with no price to value them, each once. Their marketValue is
 * null, so every total that sums the rows leaves them out, and a page names
 * them rather than show the smaller total unmarked (audit DATA-10). A row
 * whose currency has no rate is not one of them: FxMissingNote names those.
 */
export function unpricedTickers(rows: HoldingWithPnL[]): string[] {
  const out = new Set<string>();
  for (const r of rows) if (r.marketValue == null && !r.fxMissing) out.add(r.ticker);
  return [...out];
}

/**
 * Per-ticker look-through allocation weights. `country` / `sector` map a label
 * to a PERCENTAGE (0–100); weights are normalised at aggregation time. A ticker
 * absent from the map — or with an empty/all-zero sub-map — falls back to its
 * single sector/country string.
 */
export type AllocationOverrides = Record<
  string,
  { country?: Record<string, number>; sector?: Record<string, number> }
>;

/**
 * Aggregate holdings by a string field (sector, country) for the allocation
 * bars. Sorted descending by value, weights summing to 1. When `overrides`
 * supplies look-through weights, the holding's market value is SPLIT across the
 * weighted buckets instead of assigned wholesale.
 */
export function aggregateBy(
  rows: HoldingWithPnL[],
  field: 'sector' | 'country',
  overrides?: AllocationOverrides,
): Array<{ label: string; value: number; weight: number }> {
  const total = rows.reduce((s, r) => s + (r.marketValue ?? 0), 0);
  const groups: Record<string, number> = {};
  for (const r of rows) {
    const mv = r.marketValue ?? 0;
    const split = overrides?.[r.ticker]?.[field];
    // A usable split has at least one strictly-positive weight.
    const wSum = split
      ? Object.values(split).reduce((s, w) => s + (w > 0 ? w : 0), 0)
      : 0;
    if (split && wSum > 0) {
      for (const [label, w] of Object.entries(split)) {
        if (!(w > 0)) continue;
        const key = label.trim() || 'Unspecified';
        groups[key] = (groups[key] ?? 0) + mv * (w / wSum);
      }
      continue;
    }
    const key = (r[field] || 'Unspecified').toString();
    groups[key] = (groups[key] ?? 0) + mv;
  }
  return Object.entries(groups)
    .map(([label, value]) => ({ label, value, weight: total > 0 ? value / total : 0 }))
    .sort((a, b) => b.value - a.value);
}

/** A per-sector weight delta between two holding sets (scenario vs baseline). */
export interface SectorWeightDelta {
  label: string;
  base: number;
  scen: number;
  delta: number;
}

/**
 * Sector-weight diff between two holding sets, sorted by largest absolute shift.
 * Pure; powers ScenarioGenerator's "Sector mix shift" table.
 */
export function diffSectorWeights(
  baseRows: HoldingWithPnL[],
  scenRows: HoldingWithPnL[],
  overrides?: AllocationOverrides,
): SectorWeightDelta[] {
  const base = aggregateBy(baseRows, 'sector', overrides);
  const scen = aggregateBy(scenRows, 'sector', overrides);
  const all = new Set([...base.map((b) => b.label), ...scen.map((s) => s.label)]);
  return [...all].map((label) => {
    const b = base.find((x) => x.label === label)?.weight ?? 0;
    const s = scen.find((x) => x.label === label)?.weight ?? 0;
    return { label, base: b, scen: s, delta: s - b };
  }).sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta));
}
